import React, { useEffect, useRef, useState } from 'react';
import { OptimizerResult, SearchSettings, SkillActivation } from '../types/optimizer';
import { optimizeInBrowser } from '../optimizer/client';
import { difficultyName, searchSongLevels } from '../optimizer/songSearch';
import type { SongSearchProgress, SongSearchResult } from '../optimizer/songSearch';
import CardEditor from './CardEditor';
import { loadDefaultLibrary, readSavedLibrary } from '../optimizer/cardLibrary';
import type { CardLibrary } from '../optimizer/cardLibrary';

const defaults: SearchSettings = {
    user_level: 300, pro_level: 20, song_type: 2, card_count: 3,
    gauge_support: false, stock: true, reaction_time: 0.1,
};

interface Props {
    chartId: string;
    result: OptimizerResult | null;
    onResult: (result: OptimizerResult | null) => void;
    onSelect: (activation: SkillActivation) => void;
    selected: SkillActivation | null;
    songResult: SongSearchResult | null;
    onSongResult: (result: SongSearchResult | null) => void;
    onOpenLevel: (chartId: string) => void;
    showHeading?: boolean;
}

export default function ComboOptimizer({ chartId, result, onResult, onSelect, selected, songResult, onSongResult, onOpenLevel, showHeading = true }: Props) {
    const [settings, setSettings] = useState<SearchSettings>(defaults);
    const [progress, setProgress] = useState<SongSearchProgress | null>(null);
    const [allLevels, setAllLevels] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const controller = useRef<AbortController | null>(null);
    const [library, setLibrary] = useState<CardLibrary | null>(readSavedLibrary);
    const [editorLibrary, setEditorLibrary] = useState<CardLibrary | null>(null);
    const [openingEditor, setOpeningEditor] = useState(false);
    const [savedMessage, setSavedMessage] = useState('');
    const openEditor = async () => {
        setOpeningEditor(true);
        setError('');
        try { setEditorLibrary(library ?? await loadDefaultLibrary()); }
        catch (err) { setError(err instanceof Error ? err.message : 'Unable to load cards'); }
        finally { setOpeningEditor(false); }
    };
    const saveCards = (next: CardLibrary) => {
        controller.current?.abort();
        setLibrary(next);
        setEditorLibrary(null);
        setLoading(false);
        setProgress(null);
        setError('');
        onResult(null);
        onSongResult(null);
        setSavedMessage('Cards saved. Find best combo to recalculate.');
    };

    useEffect(() => {
        controller.current?.abort();
        setLoading(false);
        setError('');
        setProgress(null);
        onResult(null);
        return () => controller.current?.abort();
    }, [chartId, onResult]);

    const calculate = async (e: React.FormEvent) => {
        e.preventDefault();
        controller.current?.abort();
        const request = new AbortController();
        controller.current = request;
        setLoading(true);
        setError('');
        setProgress(null);
        setSavedMessage('');
        onResult(null);
        onSongResult(null);
        try {
            const options = {
                signal: request.signal,
                onProgress: (next: SongSearchProgress) => { if (!request.signal.aborted) setProgress(next); },
            };
            if (allLevels) {
                const optimizeWithCards: typeof optimizeInBrowser = (id, currentSettings, currentOptions) =>
                    optimizeInBrowser(id, currentSettings, { ...currentOptions, library: library ?? undefined });
                const data = await searchSongLevels(chartId, settings, optimizeWithCards, options);
                if (!request.signal.aborted) onSongResult(data);
            } else {
                const data = await optimizeInBrowser(chartId, settings, { ...options, library: library ?? undefined });
                if (!request.signal.aborted) onResult(data);
            }
        } catch (err) {
            if (!request.signal.aborted) setError(err instanceof Error ? err.message : 'Unable to calculate combo');
        } finally {
            if (!request.signal.aborted) setLoading(false);
        }
    };
    const numberField = (key: keyof SearchSettings, label: string, min: number, max: number, step = 1) => (
        <label className="text-xs text-gray-600">
            {label}
            <input className="block w-full border rounded px-2 py-1 mt-1" type="number" aria-label={label}
                min={min} max={max} step={step} required value={Number(settings[key])}
                onChange={e => setSettings({ ...settings, [key]: Number(e.target.value) })} />
        </label>
    );
    const best = result?.best;
    return (
        <aside className="bg-white text-left flex flex-col w-full" aria-label="Combo optimizer">
            <div className="p-3 border-b">
                {showHeading && <h2 className="font-bold text-gray-800">Best combo</h2>}
                <p className="text-xs text-gray-500 mt-1">Find the strongest combo, calculated in your browser.</p>
                <button type="button" disabled={openingEditor || loading} className="w-full border border-blue-200 text-blue-700 rounded py-2 text-sm mt-3 disabled:opacity-50" onClick={openEditor}>
                    {openingEditor ? 'Loading cards…' : 'Edit cards'}
                </button>
                <p className="text-xs text-gray-500 mt-1">{library ? `${library.cards.filter(c => c.enabled).length} setup cards · ${library.bombs.filter(c => c.enabled).length} bombs included` : 'Using default card inventory'}</p>
                {savedMessage && <p role="status" className="text-xs text-green-700 mt-2">{savedMessage}</p>}
                <form onSubmit={calculate} className="mt-3">
                    <label className="flex items-center gap-2 text-xs text-gray-700 mb-3">
                        <input type="checkbox" checked={allLevels} onChange={e => setAllLevels(e.target.checked)} />
                        Search all 3 levels
                    </label>
                    <details>
                        <summary className="text-xs text-blue-700 cursor-pointer">Search settings</summary>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                            {numberField('user_level', 'Player level', 1, 500)}
                            {numberField('pro_level', 'Proficiency level', 1, 21)}
                            {numberField('card_count', 'Distinct cards before bomb', 1, 4)}
                            {numberField('reaction_time', 'Reaction time (s)', 0, 2, .05)}
                            <label className="text-xs text-gray-600">Song type
                                <select className="block border rounded px-2 py-1 mt-1 w-full" value={settings.song_type}
                                    onChange={e => setSettings({ ...settings, song_type: Number(e.target.value) })}>
                                    <option value={0}>Type 0 (40%)</option><option value={1}>Type 1 (50%)</option><option value={2}>Type 2 (48%)</option>
                                </select>
                            </label>
                            <div className="text-xs flex flex-col justify-center gap-2">
                                <label><input type="checkbox" checked={settings.stock} onChange={e => setSettings({ ...settings, stock: e.target.checked })} /> Stock</label>
                                <label><input type="checkbox" checked={settings.gauge_support} onChange={e => setSettings({ ...settings, gauge_support: e.target.checked })} /> Gauge support</label>
                            </div>
                        </div>
                    </details>
                    <button type="submit" disabled={loading} className="w-full bg-blue-600 text-white rounded py-2 text-sm font-semibold mt-3 disabled:opacity-50">
                        {loading ? 'Calculating…' : 'Find best combo'}
                    </button>
                    {loading && <button type="button" className="w-full text-xs text-gray-600 mt-2 underline"
                        onClick={() => { controller.current?.abort(); setLoading(false); setProgress(null); }}>Cancel search</button>}
                </form>
                {loading && <p role="status" className="text-xs text-gray-500 mt-2">{progress?.label && `${progress.label} · `}{progress && progress.total > 0 ? `${progress.completed.toLocaleString()} / ${progress.total.toLocaleString()} combos searched…` : 'Loading chart and card data…'}</p>}
                {error && <div role="alert" className="text-xs text-red-700 mt-2">{error}<p className="mt-1">Check the search settings and that the chart files are available.</p></div>}
                {(result || songResult) && !best && <p role="status" className="text-xs mt-3">No feasible combo for this difficulty under these settings.</p>}
            </div>
            {songResult && <div className="p-3 text-xs border-b" aria-label="Difficulty comparison">
                <p className="font-semibold">Best across levels: {songResult.best ? difficultyName(songResult.best.chart_id) : 'No feasible combo'}</p>
                <p className="text-gray-500 mt-1">Ranked by score per note, matching the Python script.</p>
                <table className="w-full mt-2">
                    <thead><tr className="text-gray-500 border-b"><th className="text-left py-1">Level</th><th className="text-right">Score / note</th></tr></thead>
                    <tbody>{songResult.levels.map(level => <tr key={level.chart_id} className={`border-b ${level.chart_id === chartId ? 'bg-blue-50' : ''}`}>
                        <td className="py-2"><button className="text-blue-700 hover:underline disabled:text-gray-400 disabled:no-underline" disabled={!level.result} onClick={() => onOpenLevel(level.chart_id)}>
                            {difficultyName(level.chart_id)}{level.chart_id === songResult.best?.chart_id ? ' ★' : ''}
                        </button></td>
                        <td className="text-right">{level.result?.best ? level.result.best.score_per_note.toFixed(2) : level.error ? 'Unavailable' : 'No feasible combo'}</td>
                    </tr>)}</tbody>
                </table>
                {songResult.levels.filter(level => level.error).map(level => <p key={level.chart_id} className="mt-2 text-amber-700">{difficultyName(level.chart_id)}: {level.error}</p>)}
            </div>}
            {best && <>
                <div className="p-3 text-xs border-b">
                    <p className="text-gray-500 mb-1">{difficultyName(chartId)} · selected chart</p>
                    <p className="font-semibold text-sm">{best.combination}</p>
                    <p className="mt-1 text-orange-700">Bomb: {best.bomb} · {best.bomb_measures} measures{best.once_only ? ' · once only' : ''}</p>
                    <p className="mt-2">Leader: {best.leader} {best.leader_team && `(Team ${best.leader_team})`}</p>
                    <p>Optimizer score: {best.score.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                    <p>Score / note: {best.score_per_note.toFixed(2)}</p>
                    {best.stars && <p>Stars: {best.stars}</p>}
                    {best.event_bonus && <p>Event bonus: {best.event_bonus}</p>}
                    <p className="text-gray-500 mt-2">{result?.searched_combinations.toLocaleString()} combos searched. Score uses the script’s bomb scoring model and configured cards.</p>
                    <p className="text-gray-500 mt-1">Level {result?.settings.user_level} · Proficiency {result?.settings.pro_level} · {result?.settings.stock ? 'Stock' : 'No stock'} · {result?.settings.gauge_support ? 'Gauge support' : 'Score support'}</p>
                    {best.cards.filter(c => !c.requires_charge).map((c, i) => <p key={i} className="mt-1">{c.name}: {c.copy === 0 ? 'skill unused' : 'passive; no activation needed'}</p>)}
                </div>
                <div className="p-3 text-xs">
                    <p className="font-semibold">Skill schedule</p>
                    <p className="text-gray-500 mt-1 mb-2">Notes are numbered from 1 in the chart. Click a skill to locate and highlight it.</p>
                    <table className="w-full">
                        <thead><tr className="text-gray-500 border-b"><th className="text-left py-1">Skill / use</th><th className="text-left">Note</th><th className="text-right">Time</th></tr></thead>
                        <tbody>{best.activations.map((a, i) => <tr key={i} className={`border-b ${selected === a ? 'bg-blue-100' : ''}`}>
                            <td className="py-2"><button className={`text-left hover:underline ${a.kind === 'bomb' ? 'text-orange-700' : 'text-violet-700'}`} onClick={() => onSelect(a)}>
                                {i + 1}. {a.skill} #{a.copy}<span className="block text-gray-500">Cycle {a.cycle} · measure {a.measure}</span>
                            </button></td>
                            <td>{a.phase} #{a.note_number}<span className="block text-gray-500">Lane {a.lane}</span></td>
                            <td className="text-right font-mono">{a.time.toFixed(2)}s</td>
                        </tr>)}</tbody>
                    </table>
                </div>
            </>}
            {editorLibrary && <CardEditor library={editorLibrary} onSave={saveCards} onClose={() => setEditorLibrary(null)} />}
        </aside>
    );
}
